import { useCallback, useEffect, useState } from "react";
import { getCanonicalSlugKey, normalizeSlug } from "../utils/pageConfig";
import ContentService from "../utils/contentService";
import { ensureCatalogAuth } from "../../../services/backend/authService";
import logger from "../../../shared/utils/logger";

export const useContentPages = (contentVersion = 0) => {
  const [, setContentReady] = useState(false);

  useEffect(() => {
    ensureCatalogAuth()
      .then(() => ContentService.init())
      .then(() => setContentReady(true));
  }, []);

  const getPagesByLanding = useCallback((landingId) => {
    return ContentService.getPagesForLanding(landingId);
  }, [contentVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const getPageBySlug = useCallback((landingId, slug) => {
    const normalizedSlug = getCanonicalSlugKey(slug);
    if (!normalizedSlug) return null;
    const currentPages = getPagesByLanding(landingId);
    return currentPages.find((page) => getCanonicalSlugKey(page?.slug) === normalizedSlug) || null;
  }, [getPagesByLanding]);

  const getPageBySlugFromBackend = useCallback(async (slug) => {
    const normalizedSlug = normalizeSlug(slug);
    if (!normalizedSlug) return null;

    try {
      const page = await ContentService.getPageBySlugFromBackend(normalizedSlug);
      return page;
    } catch (err) {
      logger.warn("getPageBySlugFromBackend failed:", err);
      return null;
    }
  }, []);

  const upsertPageConfig = useCallback((landingId, pageInput) => {
    const normalizedSlug = normalizeSlug(pageInput?.slug || "");
    const canonicalSlug = getCanonicalSlugKey(normalizedSlug);
    if (!landingId || !normalizedSlug) return false;
    const normalizedPage = {
      slug: normalizedSlug,
      navLabel: pageInput?.navLabel || pageInput?.title || normalizedSlug,
      title: pageInput?.title || pageInput?.navLabel || "Página",
      footerColumn: Number.isInteger(pageInput?.footerColumn) ? pageInput.footerColumn : 0,
      isHome: pageInput?.isHome === true,
      textOnly: pageInput?.textOnly === true,
      sections: Array.isArray(pageInput?.sections) ? pageInput.sections : [],
    };

    const currentPages = ContentService.getPagesForLanding(landingId);
    const existingIndex = currentPages.findIndex((page) => getCanonicalSlugKey(page?.slug) === canonicalSlug);
    if (existingIndex >= 0) {
      const updated = [...currentPages];
      updated[existingIndex] = normalizedPage;
      ContentService.patchLandingPages(landingId, updated);
    } else {
      ContentService.patchLandingPages(landingId, [...currentPages, normalizedPage]);
    }
    return true;
  }, []);

  const deletePageConfig = useCallback((landingId, slug) => {
    const normalizedSlug = normalizeSlug(slug);
    const canonicalSlug = getCanonicalSlugKey(normalizedSlug);
    if (!landingId || !normalizedSlug) return;
    const currentPages = ContentService.getPagesForLanding(landingId);
    ContentService.patchLandingPages(
      landingId,
      currentPages.filter((page) => getCanonicalSlugKey(page?.slug) !== canonicalSlug)
    );
  }, []);

  return {
    getPagesByLanding,
    getPageBySlug,
    getPageBySlugFromBackend,
    upsertPageConfig,
    deletePageConfig,
  };
};
